import type {
  AIAvailability,
  AIProvider,
  SystemRequirementsReport,
} from "@reflecto/client-core/ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { isAbortError } from "../../utils/abortError";

export interface UseAIStatusOptions {
  provider: AIProvider;
  // Storage transport via host-supplied callbacks — keeps this hook a pure
  // headless wrapper with no @reflecto/client-core StorageAdapter dependency.
  loadInitial: () => Promise<{ lastAvailability?: AIAvailability }>;
  persistLastAvailability: (next: AIAvailability) => Promise<void>;
}

export interface UseAIStatusResult {
  availability: AIAvailability;
  requirements: SystemRequirementsReport | null;
  progressLoaded?: number;
  progressTotal?: number;
  errorMessage?: string;
  onDownload: () => Promise<void>;
  onCancel: () => void;
  onRetry: () => Promise<void>;
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function useAIStatus(options: UseAIStatusOptions): UseAIStatusResult {
  const { provider, loadInitial, persistLastAvailability } = options;
  const [availability, setAvailability] = useState<AIAvailability>("unsupported");
  const [requirements, setRequirements] = useState<SystemRequirementsReport | null>(null);
  const [progressLoaded, setProgressLoaded] = useState<number | undefined>();
  const [progressTotal, setProgressTotal] = useState<number | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const downloadAbort = useRef<AbortController | null>(null);
  const unmounted = useRef(false);
  // Mirrors what's in storage; skip redundant persists (each opens an IDB write
  // on web). Seeded on mount from loadInitial.
  const lastPersisted = useRef<AIAvailability | undefined>(undefined);

  // `shouldAbort` lets the mount effect pass its own `cancelled` flag so a stale
  // refresh from a superseded effect run can't write state after unmounted.current
  // is reset to false on re-run. Event handlers omit it (unmounted.current suffices).
  const refresh = useCallback(
    async (shouldAbort?: () => boolean) => {
      const aborted = () => unmounted.current || shouldAbort?.() === true;
      const reqs = await provider.checkRequirements();
      if (aborted()) return;
      setRequirements(reqs);
      const probed = reqs.ok ? await provider.checkAvailability() : "requirements-unmet";
      if (aborted()) return;
      // Platform may report "downloading" if another tab drove it; with no local
      // AbortController the Cancel button can't work, so surface "downloadable".
      const isLocalDownload = downloadAbort.current !== null;
      const effective: AIAvailability =
        probed === "downloading" && !isLocalDownload ? "downloadable" : probed;
      setAvailability(effective);
      if (aborted() || lastPersisted.current === effective) return;
      lastPersisted.current = effective;
      await persistLastAvailability(effective);
    },
    [provider, persistLastAvailability]
  );

  // Callers MUST give `provider`, `loadInitial`, and `persistLastAvailability` a
  // stable identity — they feed effect/refresh deps, so churn means an infinite
  // re-probe loop.
  useEffect(() => {
    unmounted.current = false;
    let cancelled = false;
    (async () => {
      const initial = await loadInitial();
      if (cancelled) return;
      lastPersisted.current = initial.lastAvailability;
      // Never restore "downloading" — no live abort controller backs Cancel.
      if (initial.lastAvailability && initial.lastAvailability !== "downloading") {
        setAvailability(initial.lastAvailability);
      }
      if (cancelled) return;
      await refresh(() => cancelled);
    })().catch((err) => {
      if (!cancelled) setErrorMessage(toMessage(err));
    });
    return () => {
      cancelled = true;
      unmounted.current = true;
      // Abort in-flight download; persist/refresh also gate on unmounted.current.
      downloadAbort.current?.abort();
      downloadAbort.current = null;
    };
  }, [loadInitial, refresh]);

  const onDownload = useCallback(async () => {
    // Caller is unprotected (UI hides Download while downloading) — bail if one
    // is in flight rather than orphaning the prior AbortController.
    if (downloadAbort.current !== null) return;
    setErrorMessage(undefined);
    setProgressLoaded(0);
    setProgressTotal(undefined);
    setAvailability("downloading");
    const controller = new AbortController();
    downloadAbort.current = controller;
    let downloadOk = false;
    try {
      await provider.download({
        signal: controller.signal,
        onProgress: (loaded, total) => {
          if (unmounted.current) return; // progress events can fire post-unmount
          setProgressLoaded(loaded);
          if (total !== undefined) setProgressTotal(total);
        },
      });
      downloadOk = true;
    } catch (err) {
      // Identity-guarded clear BEFORE refresh() so the no-local-controller remap
      // fires (onCancel may have already nulled/replaced the ref).
      if (downloadAbort.current === controller) downloadAbort.current = null;
      if (!unmounted.current && !isAbortError(err)) setErrorMessage(toMessage(err));
    }
    // refresh is the single source of truth for post-download availability; skip
    // when unmounted to avoid a wasted checkRequirements probe.
    if (!unmounted.current) {
      try {
        await refresh();
      } catch (refreshErr) {
        // Surface only on download success AND while still mounted; else the
        // download error above is the root cause.
        if (downloadOk && !unmounted.current) setErrorMessage(toMessage(refreshErr));
      }
    }
    // Identity-guarded clear: a re-entrant onDownload would install a new
    // controller; an unconditional clear would orphan it and break onCancel.
    if (downloadAbort.current === controller) downloadAbort.current = null;
  }, [provider, refresh]);

  const onCancel = useCallback(() => {
    downloadAbort.current?.abort();
    downloadAbort.current = null; // symmetric with other cleanup sites; lets refresh remap
    setProgressLoaded(undefined);
    setProgressTotal(undefined);
    // Optimistic drop out of "downloading" so the bar doesn't stick at 0 until
    // onDownload's catch → async refresh() resolves.
    setAvailability("downloadable");
  }, []);

  const onRetry = useCallback(async () => {
    setErrorMessage(undefined);
    try {
      await refresh();
    } catch (err) {
      setErrorMessage(toMessage(err));
    }
  }, [refresh]);

  return {
    availability,
    requirements,
    progressLoaded,
    progressTotal,
    errorMessage,
    onDownload,
    // onCancel/onRetry have no consumer — the Glance error card's Retry goes via useGlance.surface(),
    // not these hooks; unused — no cancel-button surface yet.
    onCancel,
    onRetry,
  };
}
